import type { ButtonBaseActions, SxProps, Theme } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import { useRef } from "react";
import {
  type Control,
  Controller,
  type RegisterOptions,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface ReactHookRadioGroupProps {
  name: string;
  label: string;
  required?: boolean;
  rules?: Exclude<
    RegisterOptions,
    "valueAsNumber" | "valueAsDate" | "setValueAs"
  >;
  options: { label: string; value: any }[];
  sx?: SxProps<Theme>;
  direction?: "column" | "column-reverse" | "row" | "row-reverse";
  control?: Control<any>;
  onChangeCallback?: (value: string) => void;
}

export function ReactHookRadioGroup({
  label,
  name,
  options,
  rules = {},
  required = false,
  sx,
  direction = "column",
  control,
  onChangeCallback,
}: ReactHookRadioGroupProps) {
  const { t } = useTranslation();
  const selectOptions = options;
  const actionRef = useRef<ButtonBaseActions | null>(null);

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required && "Next:Core:FieldRequiredValidationMessage",
        ...rules,
      }}
      render={({
        field: { onChange, ref, ...field },
        fieldState: { error },
      }) => (
        <FormControl component="fieldset" error={Boolean(error)} sx={sx}>
          <FormLabel component="legend">
            {`${required ? "* " : ""}${label}`}
          </FormLabel>
          <RadioGroup
            {...field}
            onChange={(e, value) => {
              onChange(e);
              if (typeof onChangeCallback === "function") {
                onChangeCallback(value);
              }
            }}
          >
            <Stack direction={direction} flexWrap="wrap">
              {selectOptions.map(
                ({ label: formControlLabel, value }, index) => {
                  const inputRef = index === 0 ? ref : undefined;
                  const radioAction = index === 0 ? actionRef : undefined;
                  const onFocus =
                    index === 0
                      ? () => {
                          if (actionRef.current) {
                            actionRef.current.focusVisible();
                          }
                        }
                      : undefined;
                  return (
                    <FormControlLabel
                      key={value}
                      value={value}
                      label={formControlLabel}
                      control={
                        <Radio
                          size="small"
                          inputRef={inputRef}
                          onFocus={onFocus}
                          action={radioAction}
                        />
                      }
                    />
                  );
                }
              )}
            </Stack>
          </RadioGroup>
          {Boolean(error) && (
            <FormHelperText sx={{ color: "error.main" }}>
              {error?.message && t(error.message)}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}
